import { DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { API_URLS, APIENDPOINT } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { AuthService } from '@core/auth.service';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import { screenSizeType } from '@models/common.interface';
import * as fromUserProfile from '@modules/shared/user-profile.reducer';
import * as userProfileAction from '@modules/shared/user-profile.action';
import {
  ApplicationStatusEnums,
  PERMISSION_TYPE
} from '@shared/enums/common.enum';
import { FirebaseService } from '@modules/shared/services/firebase.service';
import { PERMISSION_KEYS } from '@modules/shared/constants/permissions.contant';
import {
  Fields,
  VERIFICATION_STATUS,
  HEADER_LABELS,
  LEGAL_LINK_LABELS,
  PROFILE_CONST
} from '@constants/constant';
import { environment } from '@environments/environment';
import { appRoutingUrl } from '@enums/comman.enums';
import { DataService } from '@services/data.service';
import { HeaderFooterStateService } from '@modules/shared/services/header-footer-state.service';
import { ROUTE_PATHS } from '@constants/routes';
import { CREATORS_CONST } from '@modules/creators-home/constants/creatorsConstants';
import { IndexedDbService } from '@services/indexed-db.service';
import { CacheService } from '@shared/services/cache.service';
import { ApiCacheService } from '@core/cache/api-cache.service';
import { MarketingPageStateService } from '@modules/shared/services/marketing-page-state.service';
import { SIDEBAR_TABS } from '@modules/shared/constants/tab-approval.constant';
import { InfoModalComponent } from '@components/info-modal/info-modal.component';
import { VerifyOTPModalComponent } from '@modules/edit-preference/components/verify-otp-modal/verify-otp-modal.component';

@Component({
  selector: 'app-header-user-menu',
  templateUrl: './header-user-menu.component.html',
  styleUrls: ['./header-user-menu.component.scss']
})
export class HeaderUserMenuComponent implements OnInit, OnDestroy {
  @Input() themeColor: string | null = null;
  @Input() backgroundColor: string | null = null;
  @Input() isMarketingPageVisible = false;
  @Output() menuOpenChange = new EventEmitter<boolean>();

  @ViewChild('verifyOtpModal') verifyOtpModal: VerifyOTPModalComponent;
  @ViewChild('creatorRequirementsModal') creatorRequirementsModal;

  userProfileData = {} as any;
  delegatedAccessList: any[] = [];
  delegatedUserInProgress = null;
  verifyOtpPayload: Record<string, unknown> = {};
  showDelegateProfiles = false;
  isAdminAccess = false;
  isWhitelabel = false;
  whitelabelUserData: any;
  hasCreatorWhitelabel = false;
  showDelegationTab = false;
  isCreatorRequestResubmitted = false;
  currentTheme: string = Application.THEME_DARK as string;
  scrSize = 1220;
  logoutSubscription: Subscription;

  readonly SIDEBAR_TABS = SIDEBAR_TABS;
  readonly CREATORS_CONST = CREATORS_CONST;
  readonly permissionKeys = PERMISSION_KEYS;
  readonly PERMISSION_TYPE = PERMISSION_TYPE;
  readonly fields = Fields;
  readonly headerLabels = HEADER_LABELS;
  readonly legalLinkLabels = LEGAL_LINK_LABELS;
  readonly ROUTE_PATHS = ROUTE_PATHS;
  readonly API_URLS = API_URLS;
  readonly themes = PROFILE_CONST.THEMES;
  readonly applicationStatusEnums = ApplicationStatusEnums;
  readonly screenSize: screenSizeType = {
    desktop: Application.NINE_HUNDRED_NINETY_TWO as number,
    mobile: Application.NINE_HUNDRED_NINETY_TWO as number
  };

  private failureModalField: any;
  private successModalField: any;
  private pendingModalField: any;
  private applicationRejectModalField: any;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly authService: AuthService,
    public readonly cs: CommonService,
    private readonly router: Router,
    private readonly acs: ApiCallService,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    private readonly firebaseService: FirebaseService,
    private readonly dataService: DataService,
    private readonly headerFooterStateService: HeaderFooterStateService,
    private readonly modalService: BsModalService,
    private readonly indexedDbService: IndexedDbService,
    private readonly cacheService: CacheService,
    private readonly apiCacheService: ApiCacheService,
    private readonly marketingPageStateService: MarketingPageStateService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  @HostBinding('style.--custom-theme-color')
  get cssThemeColor(): string | null {
    return this.themeColor;
  }

  @HostBinding('style.--custom-background-color')
  get cssBackgroundColor(): string | null {
    return this.backgroundColor;
  }

  @HostBinding('style.--header-user-menu-overlay-top')
  overlayTop = '50px';

  isMenuOpen = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    if (this.cs.isBrowser) {
      this.scrSize = (event.target as Window).innerWidth;
      if (this.isMenuOpen) {
        this.syncOverlayTop();
      }
    }
  }

  ngOnInit(): void {
    if (this.cs.isBrowser) {
      this.scrSize = window.innerWidth;
      this.isAdminAccess = !!this.authService.userContext?.fm_user;
      this.isWhitelabel = this.authService.isWhiteLabelWebsite();
      this.subscribeLogout();
    }

    this.store
      .select(fromUserProfile.getUserProfileItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data && !Array.isArray(data) && typeof data === 'object') {
          this.userProfileData = data;
          this.syncThemeFromProfile();
        }
      });

    this.headerFooterStateService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.userProfileData = data;
          this.syncThemeFromProfile();
        }
      });

    this.headerFooterStateService.stateSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state.themeColor !== undefined) {
          this.themeColor = state.themeColor;
        }
        if (state.backgroundColor !== undefined) {
          this.backgroundColor = state.backgroundColor;
        }
        if (state.reTriggerNeeded && this.authService.isAuthenticated()) {
          this.fetchUserProfile();
        }
      });

    this.marketingPageStateService.isMarketingPageVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isMarketingPageVisible = visible;
      });

    this.cs.creatorWhitelabelData
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.hasCreatorWhitelabel = !!data;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.authService.isAuthenticated()) {
          this.fetchUserProfile();
        }
      });

    const config = this.cs.getWhiteLabelConfiguration();
    if (config) {
      this.whitelabelUserData = config.user;
    }

    if (this.authService.isAuthenticated()) {
      this.fetchUserProfile();
      this.getAccessList();
      this.dataService.getDelegatedAccessList();
    }
  }

  ngOnDestroy(): void {
    this.logoutSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }


  onHeaderOpen(isOpened: any) {
    if (isOpened) {
      this.delegatedAccessList = [...this.dataService.delegateAccessList];

      if (this.userProfileData.delegate_user?.type?.length) {
        const parentUser = this.userProfileData.delegate_user;
        this.delegatedAccessList.unshift({
          user: { ...parentUser },
          isParent: true
        });
        if (parentUser?.sk) {
          this.delegatedAccessList = this.delegatedAccessList.map((item) => {
            if (item.user.gsi4pk === this.userProfileData.gsi4pk) {
              item.isSwitchedUser = true;
              item.isParent = false;
            }
            return item;
          });
        }
      }

      if (this.isWhitelabel) {
        this.delegatedAccessList = this.delegatedAccessList.filter(delegatedUser =>
          (delegatedUser.user.gsi4pk === this.whitelabelUserData.gsi4pk) || delegatedUser.isParent

        );
      }
    }

    this.menuOpenChange.emit(isOpened);
  }


  // private populateDelegateAccessList(): void {
  //   this.delegatedAccessList = [...this.dataService.delegateAccessList];

  //   if (this.userProfileData.delegate_user?.type?.length) {
  //     const parentUser = this.userProfileData.delegate_user;
  //     this.delegatedAccessList.unshift({
  //       user: { ...parentUser },
  //       isParent: true
  //     });
  //     if (parentUser?.sk) {
  //       this.delegatedAccessList = this.delegatedAccessList.map((item) => {
  //         if (item.user.gsi4pk === this.userProfileData.gsi4pk) {
  //           item.isSwitchedUser = true;
  //           item.isParent = false;
  //         }
  //         return item;
  //       });
  //     }
  //   }

  //   if (this.isWhitelabel) {
  //     this.delegatedAccessList = this.delegatedAccessList.filter(
  //       (delegatedUser) =>
  //         delegatedUser.user.gsi4pk === this.whitelabelUserData?.gsi4pk ||
  //         delegatedUser.isParent
  //     );
  //   }
  // }

  private syncOverlayTop(): void {
    if (!this.cs.isBrowser || window.innerWidth >= 992) {
      this.overlayTop = '50px';
      return;
    }

    let top = 50;
    const mainHeader = this.document.querySelector(
      'app-header .header, .header'
    ) as HTMLElement | null;

    if (mainHeader) {
      top = mainHeader.getBoundingClientRect().bottom;
    }

    const coverHeader = this.document.querySelector(
      '.header-layout'
    ) as HTMLElement | null;

    if (coverHeader) {
      top = Math.max(top, coverHeader.getBoundingClientRect().bottom);
    }

    const insideHeader = this.document.querySelector(
      '.inside-header'
    ) as HTMLElement | null;

    if (insideHeader) {
      const insideHeaderStyle = window.getComputedStyle(insideHeader);
      if (
        insideHeaderStyle.display !== 'none' &&
        insideHeaderStyle.visibility !== 'hidden' &&
        insideHeaderStyle.position === 'fixed'
      ) {
        top = Math.max(top, insideHeader.getBoundingClientRect().bottom);
      }
    }

    this.overlayTop = `${Math.ceil(top)}px`;
  }

  logOut(): void {
    this.cs.closeMusicPlayerModal.next(true);
    this.logoutSubscription?.unsubscribe();
    if (this.authService.isAuthenticated()) {
      this.firebaseService?.logOutToken();
    }
    if (this.whitelabelUserData) {
      this.router.navigate(['']);
      if (this.cs.status) {
        this.cs.status = false;
      }
    } else {
      this.updateProfileTheme(Application.THEME_DARK);
      this.navigateToLogin();
    }
    this.apiCacheService.clearAllCache();
    this.store.dispatch(new userProfileAction.SetUserProfile(null));
    this.cacheService.clearAllCache();
    this.authService.removeDetails();
    this.authService.setAuthHistory('');
    this.authService.setTokenFor2FA('');
    this.modalService.hide();
  }

  editPreferences(): void {
    if (
      this.userProfileData?.is_creator &&
      !this.authService.isWhiteLabelWebsite()
    ) {
      this.router.navigate([ROUTE_PATHS.EDIT_PREFERENCE_WHITELABEL]);
      return;
    }
    this.router.navigate(['/edit-preference'], {
      queryParams: { profile: 'true' }
    });
  }

  navigateToMySite(): void {
    this.router.navigate([
      `/${this.userProfileData?.user_name || this.userProfileData?.refer_id}`
    ]);
  }

  navigateToBecomeACreatorPage(): void {
    if (this.isCreatorRequestResubmitted) {
      this.router.navigate([appRoutingUrl.BECOME_A_CREATOR]);
      return;
    }
    if (this.isCreatorRequestPending) {
      this.openVeriffStatusModals();
    }
    if (this.isCreatorRequestFailed) {
      this.openModal(this.applicationRejectModalField);
    } else if (!this.userProfileData?.admin_approval_status) {
      this.creatorRequirementsModal?.openModal();
    }
  }

  switchTheme(theme: { value: string }): void {
    if (this.userProfileData?.is_white_label || this.cs.isWhiteLabelWeb) {
      return;
    }
    if (this.currentTheme === theme.value) {
      return;
    }

    if (this.cs.isBrowser) {
      this.document.body.classList.remove(this.currentTheme);
      this.document.body.classList.add(theme.value);
    }
    this.currentTheme = theme.value;
    this.updateProfileTheme(theme.value);
    this.authService.setUserProfileTheme(theme.value);
    this.headerFooterStateService.emitThemeChange(theme.value);

    this.acs
      .put(API_URLS.getUsers, { profile_theme: theme.value }, false)
      .subscribe();
  }

  selectDelegateUser(delegatedUser: any): void {
    if (delegatedUser.isSwitchedUser) {
      return;
    }
    this.delegatedUserInProgress = delegatedUser;
    if (
      !this.dataService.verifiedDelegatedAccessToken.includes(
        this.firebaseService.deviceToken
      ) &&
      !delegatedUser.isParent
    ) {
      this.verifyOtpPayload = { device_id: this.firebaseService.deviceToken };
      this.verifyOtpModal.openModal();
    } else {
      this.switchUser();
    }
  }

  preventDefault(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  viewDelegateProfile(event: Event): void {
    this.preventDefault(event);
    this.showDelegateProfiles = !this.showDelegateProfiles;
  }

  switchUser(): void {
    if (!this.cs.isBrowser) {
      return;
    }

    if (this.delegatedUserInProgress?.isParent) {
      const token =
        JSON.parse(localStorage.getItem(Application.FM_PRIMARY_USER)) ||
        this.indexedDbService.getValueFromIndexedDb(Application.FM_PRIMARY_USER);
      if (token?.fm_user) {
        this.authService.setTokenFor2FA(token.fm_user);
        const loginResponse = {
          user: this.delegatedUserInProgress.user,
          token: token?.fm_user
        };
        this.cs.setLoginDetails({
          res: loginResponse,
          whiteLabelConfigData: null,
          isRemember: false
        });
        this.authService.setUser(loginResponse, false);
        window.location.reload();
      }
      return;
    }

    this.acs
      .post(
        API_URLS.switchToDelegate,
        {
          delegator_id: this.delegatedUserInProgress?.user?.gsi4pk,
          device_id: this.firebaseService.deviceToken
        },
        false
      )
      .subscribe({
        next: (res) => {
          if (!res.status) {
            return;
          }
          const data = res.data;
          if (
            !(
              JSON.parse(localStorage.getItem(Application.FM_PRIMARY_USER)) ||
              this.indexedDbService.getValueFromIndexedDb(
                Application.FM_PRIMARY_USER
              )
            )
          ) {
            const primaryUser =
              localStorage.getItem(Application.FM_PRIMARY_USER) ||
              this.indexedDbService.getValueFromIndexedDb(Application.FM_USER);
            this.authService.primaryUserToken = primaryUser;
            this.indexedDbService.setIndexedDbData(
              Application.FM_PRIMARY_USER,
              primaryUser
            );
            localStorage.setItem(
              Application.FM_PRIMARY_USER,
              JSON.stringify(primaryUser)
            );
          }

          this.authService.setTokenFor2FA(data?.token);
          const loginResponse = {
            ...res,
            user: data.user,
            token: data.token
          };
          this.cs.setLoginDetails({
            res: loginResponse,
            whiteLabelConfigData: null,
            isRemember: false
          });
          this.authService.setUser(loginResponse, false);
          if (!this.authService.isWhiteLabelWebsite()) {
            this.router
              .navigate([`/${loginResponse?.user?.user_name}`])
              .then(() => window.location.reload());
          } else {
            window.location.reload();
          }
        },
        error: (err) => {
          this.cs.showErrorMessage(err.message);
        }
      });
  }

  get isCreatorRequestFailed(): boolean {
    return (
      this.userProfileData?.admin_approval_status ===
      this.applicationStatusEnums.FAILED
    );
  }

  get isCreatorRequestApproved(): boolean {
    return (
      this.userProfileData?.admin_approval_status ===
      this.applicationStatusEnums.APPROVED
    );
  }

  get isCreatorRequestPending(): boolean {
    return (
      this.userProfileData?.admin_approval_status ===
      this.applicationStatusEnums.PENDING
    );
  }

  get checkPendingCreatorApproval(): boolean {
    if (
      this.userProfileData?.admin_approval_status ===
      ApplicationStatusEnums.PENDING
    ) {
      return false;
    }
    return true;
  }

  private subscribeLogout(): void {
    this.logoutSubscription = this.cs.logout?.subscribe((res) => {
      if (res) {
        this.logOut();
      }
    });
  }

  private fetchUserProfile(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.acs
      .get(
        `${API_URLS.users}?host_name=${this.cs.isBrowser ? window.location.origin : null}`,
        false
      )
      .subscribe({
        next: (res) => {
          if (!res.status) {
            this.updateProfileTheme(Application.THEME_DARK);
            this.cs.showErrorMessage(res.message);
            return;
          }

          if (res.is_suspended) {
            this.authService.removeDetails();
            this.router.navigate(['auth/login']);
            return;
          }

          const mergeObj = {
            ...res.creator_detail,
            ...res.data,
            is_white_label: res.is_white_label,
            is_host_owner: res.is_host_owner,
            delegate_user: res.delegate_user
          };
          this.userProfileData = mergeObj;
          this.userProfileData.isAbove18 = this.cs.isUser18Above(
            this.userProfileData.birth_date
          );
          this.authService.userData = mergeObj;
          this.authService.setIsCreator(!!mergeObj.is_creator);
          this.isCreatorRequestResubmitted =
            this.userProfileData?.id_verification_status ===
            this.applicationStatusEnums.RESUBMIT;
          this.headerFooterStateService.emitUserData(mergeObj);
          this.store.dispatch(new userProfileAction.SetUserProfile(mergeObj));

          const savedTheme = this.cs.getValueFromLocalStorage('theme');
          const theme = savedTheme ? savedTheme : Application.THEME_DARK;
          this.currentTheme =
            res.data.profile_theme || theme || Application.THEME_DARK;
          this.setModalFields();
          if (!this.userProfileData?.is_white_label) {
            this.updateProfileTheme(res.data.profile_theme || theme);
          }
        },
        error: (err) => {
          this.updateProfileTheme(Application.THEME_DARK);
          if (err?.message !== Application.UNAUTHORIZED) {
            this.cs.showErrorMessage(err.message);
          }
        }
      });
  }

  private getAccessList(): void {
    this.acs
      .get(APIENDPOINT.getDelegationList(), !this.cs.isPWA())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.showDelegationTab = res.data.length >= 1;
          }
        },
        error: (err) => {
          this.cs.showErrorMessage(err);
        }
      });
  }

  private syncThemeFromProfile(): void {
    if (this.userProfileData?.profile_theme) {
      this.currentTheme = this.userProfileData.profile_theme;
    }
  }

  private updateProfileTheme(selectedTheme: string): void {
    if (this.isWhitelabel) {
      return;
    }
    this.cs.updateTheme(selectedTheme);
  }

  private navigateToLogin(): void {
    if (this.cs.isWhiteLabelWeb) {
      const username = this.cs.whiteLabelData?.user?.user_name;
      this.router.navigate([`/${username}`]);
      return;
    }
    this.router.navigate(['/']);
  }

  private openVeriffStatusModals(): void {
    const verificationStatus = this.userProfileData?.id_verification_status;
    switch (verificationStatus) {
      case VERIFICATION_STATUS.APPROVED:
        this.openModal(this.successModalField);
        break;
      case VERIFICATION_STATUS.PENDING:
        this.openModal(this.pendingModalField);
        break;
      default:
        this.openModal(this.failureModalField);
        break;
    }
  }

  private openModal(modalField: any): void {
    this.modalService.show(InfoModalComponent, {
      initialState: modalField,
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  private setModalFields(): void {
    this.failureModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      heading: 'Identity Verification Failed',
      description: `Unfortunately, your identity was not able to be verified. <br>For this reason we can\'t evaluate your application to Become a Creator. <br> If you think this is a mistake, please <a href="${environment.websiteURL}/help-faq" target="_blank" class="highlight">contact customer support</a>.`,
      iconStyleClass: 'red-bg',
      buttonField: {
        type: 'button',
        class: 'done-btn',
        label: 'Done'
      }
    };

    this.successModalField = {
      icon: './assets/images/svg/checkmark-outline.svg',
      heading: 'Your Identity Has Been Verified',
      description:
        'The TopFan team will now evaluate your application to Become a Creator and you will typically receive a status update by email within one business day. <br> You can now return to TopFan to continue exploring the community.',
      iconStyleClass: 'confirm-creator-icon-bg',
      buttonField: {
        type: 'button',
        class: 'done-btn',
        label: 'Done'
      }
    };

    this.pendingModalField = {
      icon: './assets/images/svg/pending.svg',
      heading: 'Identity Verification Pending',
      description:
        'Your identity verification is currently in progress. We are evaluating your application to Become a Creator. Please be patient as this process may take some time. If you have any questions, please <a href="' +
        environment.websiteURL +
        'help-faq" target="_blank" class="highlight">contact customer support</a>.',
      iconStyleClass: 'yellow-bg',
      buttonField: {
        type: 'button',
        class: 'done-btn',
        label: 'Done'
      }
    };

    this.applicationRejectModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      iconStyleClass: 'red-bg',
      heading: 'Your application has been declined by our team. ',
      description: `Reason: ${this.userProfileData?.admin_action_reason}<br><br> If you think this is a mistake, please <a href="${environment.websiteURL}help-faq" target="_blank" class="highlight">contact customer support</a>.`
    };
  }
}
